import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutEmployee } from '../redux/actions/authActions';

const SESSION_TIMEOUT_MINUTES = 120; // 2 hours
const CHECK_INTERVAL_MS = 60000; // Check every minute

export const useSessionTimer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Clear all localStorage items (comprehensive cleanup)
  const clearAllLocalStorage = useCallback(() => {
    // Clear auth data
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    
    // Clear survey related data
    localStorage.removeItem("surveyType");
    localStorage.removeItem("selectedCanteen");
    localStorage.removeItem("selectedSurvey");
    localStorage.removeItem("surveyData");
    localStorage.removeItem("completedSections");
    localStorage.removeItem("surveyId");
    
    // Clear any other potential survey data
    localStorage.removeItem("currentSurveySection");
    localStorage.removeItem("surveyProgress");
    localStorage.removeItem("tempSurveyData");
    
    // Clear any cached form data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('survey') || key.includes('form') || key.includes('draft'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    console.log("Cleared all session and survey data from localStorage");
  }, []);

  // Perform logout and cleanup
  const performLogout = useCallback(async () => {
    console.log("Session timeout: Logging out due to inactivity");
    
    // Clear all localStorage first
    clearAllLocalStorage();
    
    // Dispatch logout action (this will also clear localStorage in the action)
    await dispatch(logoutEmployee());
    
    // Navigate to login page
    navigate("/", { replace: true });
    
    // Show notification (optional - you can use toast here if available)
    if (window.toast) {
      window.toast.error("Session expired due to inactivity. Please login again.");
    } else {
      alert("Session expired due to inactivity. Please login again.");
    }
  }, [dispatch, navigate, clearAllLocalStorage]);

  // Reset the timer
  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      performLogout();
    }, SESSION_TIMEOUT_MINUTES * 60 * 1000);
    
    // console.log(`Session timer reset. Will timeout in ${SESSION_TIMEOUT_MINUTES} minutes`);
  }, [performLogout]);

  // Track user activity
  const handleUserActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Set up event listeners for user activity
  useEffect(() => {
    const activities = [
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
      'mousemove',
      'wheel',
      'touchmove',
      'touchcancel',
      'touchend'
    ];
    
    // Add event listeners
    activities.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    // Start the timer
    resetTimer();
    
    // Set up periodic check (optional - for extra safety)
    checkIntervalRef.current = setInterval(() => {
      const lastActivity = sessionStorage.getItem('lastActivityTime');
      const now = Date.now();
      
      if (lastActivity) {
        const inactiveTime = now - parseInt(lastActivity);
        if (inactiveTime > SESSION_TIMEOUT_MINUTES * 60 * 1000) {
          performLogout();
        }
      }
      sessionStorage.setItem('lastActivityTime', now.toString());
    }, CHECK_INTERVAL_MS);
    
    // Cleanup
    return () => {
      activities.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [handleUserActivity, resetTimer, performLogout]);

  // Return function to manually clear session
  return { clearSession: performLogout, resetTimer };
};